/*
 * Small SVG charts, no library: Sparkline (trend line with a soft fill) and BarChart (vertical bars with
 * labels and an accessible table fallback). Values are plain numbers; colours come from tokens.
 *   <Sparkline values={[3, 5, 4, 8, 6, 9]} tone="ai" />
 *   <BarChart data={[{ label: 'Oct', value: 742 }, …]} format={(n) => f.number(n)} />
 * Charts read left to right (time) in both directions, as is the convention for timelines.
 */
import { useId } from 'react'
import { cn } from '@/lib/cn'

type ChartTone = 'brand' | 'ai' | 'warning' | 'danger'
const STROKE: Record<ChartTone, string> = {
  brand: 'var(--color-brand-600)',
  ai: 'var(--color-teal-600)',
  warning: 'var(--color-warm-500)',
  danger: 'var(--color-danger-600)',
}

export function Sparkline({
  values,
  tone = 'brand',
  className,
  label,
}: {
  values: number[]
  tone?: ChartTone
  className?: string
  /** accessible summary; without it the chart is decorative */
  label?: string
}) {
  const id = useId()
  const w = 120
  const h = 36
  const max = Math.max(...values, 1)
  const min = Math.min(...values, 0)
  const span = max - min || 1
  const step = w / Math.max(1, values.length - 1)
  const pts = values.map(
    (v, i) => `${(i * step).toFixed(1)},${(h - 3 - ((v - min) / span) * (h - 6)).toFixed(1)}`,
  )
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className={cn('block h-9 w-full', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      direction="ltr"
    >
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={STROKE[tone]} stopOpacity="0.18" />
          <stop offset="1" stopColor={STROKE[tone]} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M0,${h} L${pts.join(' L')} L${w},${h} Z`} fill={`url(#${CSS.escape(id)})`} />
      <path
        d={`M${pts.join(' L')}`}
        fill="none"
        stroke={STROKE[tone]}
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

export function BarChart({
  data,
  tone = 'brand',
  format = String,
  height = 140,
  labelEvery = 1,
  className,
  label,
}: {
  data: { label: string; value: number; highlight?: boolean }[]
  tone?: ChartTone
  format?: (n: number) => string
  height?: number
  /** label every n-th bar, counted back from the last one (which always has its label) */
  labelEvery?: number
  className?: string
  label: string
}) {
  const max = Math.max(...data.map((d) => d.value), 1)
  const gap = data.length > 16 ? 'gap-0.5 sm:gap-1' : 'gap-1.5'
  const labelled = (i: number) => (data.length - 1 - i) % Math.max(1, labelEvery) === 0
  return (
    <figure className={cn('flex flex-col gap-2', className)}>
      <div dir="ltr" className={cn('flex items-end', gap)} style={{ height }} aria-hidden="true">
        {data.map((d) => (
          <div
            key={d.label}
            className="group flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
          >
            <span className="figures text-[11px] text-subtle opacity-0 transition-opacity group-hover:opacity-100">
              {format(d.value)}
            </span>
            <span
              className="w-full max-w-8 rounded-t-md transition-[height] duration-500"
              style={{
                height: `${Math.max(2, (d.value / max) * (height - 20))}px`,
                background: STROKE[tone],
                opacity: d.highlight ? 1 : 0.35,
              }}
            />
          </div>
        ))}
      </div>
      <div dir="ltr" className={cn('flex', gap)} aria-hidden="true">
        {data.map((d, i) => (
          <span
            key={d.label}
            className="min-w-0 flex-1 overflow-visible text-center text-[11px] whitespace-nowrap text-subtle"
          >
            {labelled(i) ? d.label : null}
          </span>
        ))}
      </div>
      <table className="sr-only">
        <caption>{label}</caption>
        <tbody>
          {data.map((d) => (
            <tr key={d.label}>
              <th scope="row">{d.label}</th>
              <td>{format(d.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  )
}
